import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronRight, 
  ChevronLeft, 
  CheckCircle2,
  Globe,
  Megaphone,
  Users,
  Zap,
  Phone,
  Settings,
  Target,
  TrendingUp,
  Award,
  Calendar,
  Building,
  User,
  Mail,
  Loader2,
  Briefcase,
  DollarSign,
  UserCircle
} from 'lucide-react';
import { useForm } from 'react-hook-form';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Quoteplicity Logo Component
const QuoteplicityLogo = () => (
  <img 
    src="https://customer-assets.emergentagent.com/job_agent-qualifier-form/artifacts/12vhtlnd_quoteplicity%20%282%29.svg" 
    alt="Quoteplicity" 
    className="h-8 sm:h-10 w-auto"
  />
);

// Services data - reduced to 6 core services
const services = [
  { icon: Globe, title: 'Website & Landing Pages', description: 'Conversion-optimized sites built specifically for life insurance lead capture' },
  { icon: Megaphone, title: 'Paid Advertising', description: 'Strategic campaigns across Google, Facebook, and other high-intent platforms' },
  { icon: Target, title: 'Advanced Funnels', description: 'Multi-step funnels that qualify and enrich leads before they reach you' },
  { icon: Users, title: 'Lead Management', description: 'Systems to track, score, and nurture every lead automatically' },
  { icon: Settings, title: 'CRM & Automations', description: 'GoHighLevel setup with automated follow-ups and workflows' },
  { icon: TrendingUp, title: 'Ongoing Optimization', description: 'Continuous testing and scaling to maximize your ROI' },
];

// Form options
const businessTypes = [
  { id: 'agency', label: 'Insurance Agency', description: 'Multiple agents under one organization' },
  { id: 'independent', label: 'Independent Agent', description: 'Solo practitioner or small team' },
  { id: 'bga', label: 'BGA (Broker General Agency)', description: 'Wholesale distribution organization' },
  { id: 'other', label: 'Other', description: 'Another type of business or organization' },
];

const agentCountOptions = [
  { value: '1', label: 'Just me' },
  { value: '2-5', label: '2-5 agents' },
  { value: '6-10', label: '6-10 agents' },
  { value: '11-25', label: '11-25 agents' },
  { value: '26-50', label: '26-50 agents' },
  { value: '50+', label: '50+ agents' },
];

const marketingPlatforms = [
  { id: 'facebook', label: 'Facebook/Meta' },
  { id: 'google', label: 'Google Ads' },
  { id: 'linkedin', label: 'LinkedIn' },
  { id: 'youtube', label: 'YouTube' },
  { id: 'tiktok', label: 'TikTok' },
  { id: 'seo', label: 'SEO/Organic' },
  { id: 'email', label: 'Email Marketing' },
  { id: 'none', label: 'No experience yet' },
];

const budgetOptions = [
  { value: 'under-2500', label: 'Under $2,500/month', qualified: false },
  { value: '2500-5000', label: '$2,500 - $5,000/month', qualified: false },
  { value: '5000-10000', label: '$5,000 - $10,000/month', qualified: true },
  { value: '10000-25000', label: '$10,000 - $25,000/month', qualified: true },
  { value: '25000+', label: '$25,000+/month', qualified: true },
];

const crmOptions = [
  { value: 'none', label: 'No CRM currently' },
  { value: 'gohighlevel', label: 'GoHighLevel' },
  { value: 'salesforce', label: 'Salesforce' },
  { value: 'hubspot', label: 'HubSpot' },
  { value: 'agencybloc', label: 'AgencyBloc' },
  { value: 'radiusbob', label: 'Radiusbob' },
  { value: 'other', label: 'Other' },
];

export const MarketingAgencyPage = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  
  // Set page meta tags
  useEffect(() => {
    // Title tag
    document.title = 'Life Insurance Marketing Agency | Quoteplicity';
    
    // Helper to set or update meta tags
    const setMetaTag = (name, content, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.content = content;
      return meta;
    };
    
    // Meta description
    setMetaTag('description', 'Quoteplicity Life Insurance Marketing Agency - Full-service digital marketing exclusively for life insurance professionals. 20+ years experience, 1M+ leads generated. Website design, paid advertising, lead generation, and CRM automation.');
    
    // Robots tag - index and follow
    setMetaTag('robots', 'index, follow');
    
    // Open Graph tags for social sharing
    setMetaTag('og:title', 'Life Insurance Marketing Agency | Quoteplicity', true);
    setMetaTag('og:description', 'Full-service digital marketing exclusively for life insurance professionals. 20+ years experience generating qualified leads.', true);
    setMetaTag('og:type', 'website', true);
    
  }, []);
  
  // Form state
  const [businessType, setBusinessType] = useState('');
  const [agentCount, setAgentCount] = useState('');
  const [hasWebsite, setHasWebsite] = useState(null);
  const [currentWebsite, setCurrentWebsite] = useState('');
  const [websiteError, setWebsiteError] = useState('');
  const [marketingExperience, setMarketingExperience] = useState([]);
  const [budget, setBudget] = useState('');
  const [currentCRM, setCurrentCRM] = useState('');
  const [otherCRM, setOtherCRM] = useState('');
  const [openToGoHighLevel, setOpenToGoHighLevel] = useState(null);
  
  // Contact form validation errors
  const [phoneError, setPhoneError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [contactWebsiteError, setContactWebsiteError] = useState('');
  
  const contactForm = useForm({
    defaultValues: {
      name: '',
      company_name: '',
      website: '',
      phone: '',
      email: '',
      additional_details: '',
    }
  });

  // Validation functions
  const validateWebsite = (url) => {
    if (!url) return true; // Optional field
    const urlPattern = /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)$/;
    return urlPattern.test(url);
  };

  const validateEmail = (email) => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(email);
  };

  const validatePhone = (phone) => {
    // Remove all non-digits
    const digits = phone.replace(/\D/g, '');
    
    // Must be 10 digits (US format)
    if (digits.length !== 10) {
      return { valid: false, error: 'Phone number must be 10 digits' };
    }
    
    // Check for sequential numbers (123456, 987654, etc.)
    const sequential = '01234567890123456789009876543210987654321';
    if (sequential.includes(digits)) {
      return { valid: false, error: 'Phone number cannot be sequential' };
    }
    
    // Check for all same digits (1111111111, etc.)
    if (/^(\d)\1{9}$/.test(digits)) {
      return { valid: false, error: 'Phone number cannot be all the same digit' };
    }
    
    // Check for repeating patterns (1212121212, 123123123, etc.)
    if (/^(\d{1,3})\1+$/.test(digits)) {
      return { valid: false, error: 'Phone number cannot be a repeating pattern' };
    }
    
    // Check for common fake numbers
    const fakeNumbers = ['0000000000', '1111111111', '2222222222', '3333333333', 
                         '4444444444', '5555555555', '6666666666', '7777777777',
                         '8888888888', '9999999999', '1234567890', '0987654321',
                         '5551234567', '5550001234'];
    if (fakeNumbers.includes(digits)) {
      return { valid: false, error: 'Please enter a valid phone number' };
    }
    
    return { valid: true, error: '' };
  };

  const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const handleWebsiteChange = (e) => {
    const value = e.target.value;
    setCurrentWebsite(value);
    if (value && !validateWebsite(value)) {
      setWebsiteError('Please enter a valid website URL (e.g., https://example.com)');
    } else {
      setWebsiteError('');
    }
  };

  // Pre-fill contact form website when moving to step 5
  useEffect(() => {
    if (currentStep === 5 && currentWebsite) {
      contactForm.setValue('website', currentWebsite);
    }
  }, [currentStep, currentWebsite, contactForm]);

  // Progress calculation
  const totalSteps = 5;
  const progress = ((currentStep + 1) / totalSteps) * 100;

  const canProceedStep1 = businessType !== '';
  const canProceedStep2 = agentCount !== '' && hasWebsite !== null && (!hasWebsite || (currentWebsite && !websiteError));
  const canProceedStep3 = marketingExperience.length > 0 && budget !== '';
  const canProceedStep4 = currentCRM !== '' && openToGoHighLevel !== null;

  const togglePlatform = (platformId) => {
    if (platformId === 'none') {
      setMarketingExperience(['none']);
    } else {
      setMarketingExperience(prev => {
        const filtered = prev.filter(p => p !== 'none');
        if (filtered.includes(platformId)) {
          return filtered.filter(p => p !== platformId);
        }
        return [...filtered, platformId];
      });
    }
  };

  const goBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleContactSubmit = async (data) => {
    setIsSubmitting(true);
    
    const submissionData = {
      ...data,
      business_type: businessType,
      agent_count: agentCount,
      has_website: hasWebsite,
      current_website: hasWebsite ? currentWebsite : null,
      marketing_experience: marketingExperience,
      budget: budget,
      current_crm: currentCRM === 'other' ? otherCRM : currentCRM,
      open_to_gohighlevel: openToGoHighLevel,
    };

    try {
      await axios.post(`${API}/marketing-inquiry`, submissionData);
      setSubmissionSuccess(true);
    } catch (error) {
      console.error('Submission error:', error);
      alert('There was an error submitting your inquiry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Check if budget is qualified
  const selectedBudget = budgetOptions.find(b => b.value === budget);
  const isQualified = selectedBudget?.qualified !== false;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 sm:h-20">
            <a href="https://www.quoteplicity.com/" target="_blank" rel="noopener noreferrer" className="flex-shrink-0">
              <QuoteplicityLogo />
            </a>
            <nav className="hidden md:flex items-center gap-6 text-sm font-medium text-slate-600">
              <a href="https://www.quoteplicity.com/" className="hover:text-[#10c0a5] transition-colors">Home</a>
              <a href="https://www.quoteplicity.com/about/" className="hover:text-[#10c0a5] transition-colors">About</a>
              <a href="https://www.quoteplicity.com/features/" className="hover:text-[#10c0a5] transition-colors">Features</a>
              <a href="https://www.quoteplicity.com/pricing/" className="hover:text-[#10c0a5] transition-colors">Pricing</a>
              <a href="https://www.quoteplicity.com/contact/" className="hover:text-[#10c0a5] transition-colors">Contact</a>
            </nav>
            <a 
              href="tel:6029021072" 
              className="flex items-center gap-2 text-slate-700 hover:text-[#10c0a5] font-medium"
            >
              <Phone className="w-4 h-4" />
              <span className="hidden sm:inline">(602) 902-1072</span>
            </a>
          </div>
        </div>
      </header>

      {!submissionSuccess ? (
        <>
          {/* Hero Section */}
          {currentStep === 0 && (
            <section className="relative py-16 sm:py-20 px-4 overflow-hidden bg-gradient-to-b from-slate-50 to-white">
              {/* Background decoration */}
              <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#10c0a5]/5 to-transparent" />
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#10c0a5]/5 rounded-full blur-3xl" />
              
              <div className="max-w-6xl mx-auto relative">
                <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
                  {/* Left: Text Content */}
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="text-center lg:text-left"
                  >
                    <h1 className="text-4xl sm:text-5xl lg:text-5xl font-bold text-slate-900 tracking-tight mb-6 leading-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      <span className="block mb-2">Quoteplicity</span>
                      <span className="text-[#10c0a5]">Marketing Agency</span>
                    </h1>
                    <p className="text-lg text-slate-600 mb-4">
                      We've generated <span className="font-semibold text-slate-900">millions of qualified leads</span> for life insurance agencies over the last decade. 
                      Let us build your lead generation machine.
                    </p>
                    <p className="text-sm text-slate-500 mb-8">
                      Led by <span className="font-medium">Brian Greenberg</span>, our team provides full-service marketing 
                      exclusively for the life insurance industry, or those expanding into it.
                    </p>
                    
                    {/* Quick stats */}
                    <div className="flex flex-wrap justify-center lg:justify-start gap-6 mb-8">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-[#10c0a5]">20+</div>
                        <div className="text-xs text-slate-500">Years Experience</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-[#10c0a5]">1M+</div>
                        <div className="text-xs text-slate-500">Leads Generated</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-[#10c0a5]">100%</div>
                        <div className="text-xs text-slate-500">Life Insurance Focus</div>
                      </div>
                    </div>
                  </motion.div>

                  {/* Right: Image/Visual - visible on all screens */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="relative flex justify-center lg:justify-start"
                  >
                    <div className="relative w-[280px] lg:w-full lg:max-w-none">
                      {/* Main image container */}
                      <div className="bg-gradient-to-br from-[#10c0a5] to-[#0ea892] rounded-2xl p-4 lg:p-8 shadow-2xl">
                        <div className="bg-white rounded-xl p-4 lg:p-6 shadow-lg">
                          <div className="flex items-center gap-2 lg:gap-3 mb-3 lg:mb-4">
                            <div className="w-8 h-8 lg:w-10 lg:h-10 bg-[#10c0a5]/10 rounded-full flex items-center justify-center">
                              <TrendingUp className="w-4 h-4 lg:w-5 lg:h-5 text-[#10c0a5]" />
                            </div>
                            <div>
                              <div className="text-xs lg:text-sm font-semibold text-slate-900">Lead Performance</div>
                              <div className="text-[10px] lg:text-xs text-slate-500">Last 30 days</div>
                            </div>
                          </div>
                          <div className="space-y-2 lg:space-y-3">
                            <div className="flex justify-between items-center">
                              <span className="text-xs lg:text-sm text-slate-600">New Leads</span>
                              <span className="text-xs lg:text-sm font-semibold text-slate-900">847</span>
                            </div>
                            <div className="h-1.5 lg:h-2 bg-slate-100 rounded-full overflow-hidden">
                              <div className="h-full w-4/5 bg-[#10c0a5] rounded-full" />
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-xs lg:text-sm text-slate-600">Qualified</span>
                              <span className="text-xs lg:text-sm font-semibold text-[#10c0a5]">+127%</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* Floating elements */}
                      <div className="absolute -top-2 -right-2 lg:-top-4 lg:-right-4 bg-white rounded-lg shadow-lg p-1.5 lg:p-3 border border-slate-100">
                        <div className="flex items-center gap-1.5 lg:gap-2">
                          <CheckCircle2 className="w-3.5 h-3.5 lg:w-5 lg:h-5 text-green-500" />
                          <span className="text-[10px] lg:text-sm font-medium text-slate-700">Campaign Live</span>
                        </div>
                      </div>
                      <div className="absolute -bottom-2 -left-2 lg:-bottom-4 lg:-left-4 bg-white rounded-lg shadow-lg p-1.5 lg:p-3 border border-slate-100">
                        <div className="flex items-center gap-1.5 lg:gap-2">
                          <Zap className="w-3.5 h-3.5 lg:w-5 lg:h-5 text-amber-500" />
                          <span className="text-[10px] lg:text-sm font-medium text-slate-700">Auto-nurture enabled</span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </div>

                {/* Services Section - with background */}
                <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-8 mb-12">
                  <h2 className="text-xl font-bold text-slate-900 text-center mb-8" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    What We Do For You
                  </h2>
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
                  >
                    {services.map((service, index) => {
                      const Icon = service.icon;
                      return (
                        <div 
                          key={index}
                          className="flex gap-4 p-4 rounded-xl hover:bg-slate-50 transition-all"
                        >
                          <div className="flex-shrink-0 w-12 h-12 bg-[#10c0a5]/10 rounded-xl flex items-center justify-center">
                            <Icon className="w-6 h-6 text-[#10c0a5]" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-slate-900 mb-1">{service.title}</h3>
                            <p className="text-sm text-slate-500">{service.description}</p>
                          </div>
                        </div>
                      );
                    })}
                  </motion.div>
                </div>

                {/* How It Works Section - with subtle background */}
                <div className="bg-slate-50 rounded-3xl p-8 mb-12 border border-slate-100">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.25 }}
                  >
                    <h2 className="text-2xl font-bold text-slate-900 text-center mb-8" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      How It Works
                    </h2>
                    <div className="flex flex-col md:flex-row items-stretch justify-center gap-4">
                      {[
                        { step: '1', title: 'Discovery', description: 'We learn about your agency, goals, and ideal clients' },
                        { step: '2', title: 'Strategy', description: 'We build a custom marketing plan tailored to your market' },
                        { step: '3', title: 'Launch', description: 'We deploy your campaigns, funnels, and automations' },
                        { step: '4', title: 'Scale', description: 'We optimize and grow your lead flow over time' },
                      ].map((item, index) => (
                        <div key={index} className="flex items-center">
                          <div className="bg-white rounded-xl p-5 text-center flex-1 min-w-[200px] border border-slate-200 shadow-sm">
                            <div className="w-10 h-10 bg-[#10c0a5] text-white rounded-full flex items-center justify-center font-bold text-lg mx-auto mb-3">
                              {item.step}
                            </div>
                            <h3 className="font-semibold text-slate-900 mb-2">{item.title}</h3>
                            <p className="text-sm text-slate-500">{item.description}</p>
                          </div>
                          {index < 3 && (
                            <div className="hidden md:flex items-center justify-center px-2">
                              <ChevronRight className="w-6 h-6 text-[#10c0a5]" />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </motion.div>
                </div>

                {/* Guarantee Section */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.28 }}
                  className="mb-12"
                >
                  <div className="bg-[#10c0a5] rounded-2xl p-8 sm:p-10">
                    <div className="flex flex-col sm:flex-row items-center gap-6">
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                          <CheckCircle2 className="w-8 h-8 text-white" />
                        </div>
                      </div>
                      <div className="text-center sm:text-left">
                        <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                          Our Lead Response Guarantee
                        </h3>
                        <p className="text-white/90">
                          We guarantee results when you commit to following up with leads in a timely manner. 
                          Great marketing deserves great follow-through. When you respond to leads quickly, 
                          we guarantee you'll see the ROI you're looking for.
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* CTA Section - Bolder */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="text-center bg-gradient-to-r from-slate-50 to-slate-100 rounded-2xl p-10 border border-slate-200"
                >
                  <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Ready to Scale Your Agency?
                  </h2>
                  <p className="text-slate-600 mb-6 max-w-lg mx-auto">
                    We partner with agencies investing a minimum of <span className="font-semibold text-slate-900">$5,000/month</span> in their marketing growth. 
                    Limited spots available.
                  </p>
                  <button
                    onClick={() => setCurrentStep(1)}
                    className="inline-flex items-center gap-2 px-10 py-4 bg-[#10c0a5] hover:bg-[#0ea892] text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all text-lg"
                    data-testid="get-started-btn"
                  >
                    See If We're a Fit
                    <ChevronRight className="w-5 h-5" />
                  </button>
                  
                  {/* Quoteplicity Customer Disclaimer */}
                  <p className="text-xs text-slate-400 mt-8 max-w-lg mx-auto">
                    Marketing Agency services are available exclusively to Quoteplicity software customers. 
                    Not a customer yet? <a href="https://www.quoteplicity.com/pricing/" target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-[#10c0a5] underline">View our plans</a>.
                  </p>
                </motion.div>
              </div>
            </section>
          )}

          {/* Form Steps */}
          {currentStep > 0 && (
            <section className="py-8 sm:py-12 px-4">
              <div className="max-w-2xl mx-auto">
                {/* Progress Bar */}
                <div className="mb-8">
                  <div className="flex justify-between text-sm text-slate-500 mb-2">
                    <span>Step {currentStep} of {totalSteps - 1}</span>
                    <span>{Math.round(progress)}% Complete</span>
                  </div>
                  <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                    <motion.div 
                      className="h-full bg-[#10c0a5]"
                      initial={{ width: 0 }}
                      animate={{ width: `${progress}%` }}
                      transition={{ duration: 0.3 }}
                    />
                  </div>
                </div>

                <AnimatePresence mode="wait">
                  {/* Step 1: Business Type */}
                  {currentStep === 1 && (
                    <motion.div
                      key="step1"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8"
                    >
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#10c0a5] to-[#0d9488] flex items-center justify-center shadow-lg shadow-[#10c0a5]/20">
                          <Building className="w-7 h-7 text-white" />
                        </div>
                        <div>
                          <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Tell us about your business
                          </h2>
                          <p className="text-slate-600">What type of organization are you?</p>
                        </div>
                      </div>

                      <div className="space-y-3 mb-8">
                        {businessTypes.map((type) => (
                          <div
                            key={type.id}
                            onClick={() => setBusinessType(type.id)}
                            className={`
                              relative cursor-pointer rounded-xl border-2 p-4 transition-all duration-200
                              ${businessType === type.id 
                                ? 'border-[#10c0a5] bg-[#e6faf7]/50' 
                                : 'border-slate-200 hover:border-[#10c0a5]/50'
                              }
                            `}
                            data-testid={`business-type-${type.id}`}
                          >
                            {businessType === type.id && (
                              <CheckCircle2 className="absolute top-4 right-4 w-5 h-5 text-[#10c0a5]" />
                            )}
                            <h3 className="font-semibold text-slate-900">{type.label}</h3>
                            <p className="text-sm text-slate-500">{type.description}</p>
                          </div>
                        ))}
                      </div>

                      <div className="flex justify-between">
                        <button
                          onClick={() => setCurrentStep(0)}
                          className="flex items-center gap-2 px-6 py-3 text-slate-600 hover:text-slate-900 font-medium transition-colors"
                        >
                          <ChevronLeft className="w-5 h-5" />
                          Back
                        </button>
                        <button
                          onClick={() => setCurrentStep(2)}
                          disabled={!canProceedStep1}
                          className="flex items-center gap-2 px-6 py-3 bg-[#10c0a5] hover:bg-[#0ea892] text-white font-medium rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Continue
                          <ChevronRight className="w-5 h-5" />
                        </button>
                      </div>
                    </motion.div>
                  )}

                  {/* Step 2: Team Size & Website */}
                  {currentStep === 2 && (
                    <motion.div
                      key="step2"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8"
                    >
                      <div className="flex items-center gap-4 mb-6">
                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#10c0a5] to-[#0d9488] flex items-center justify-center shadow-lg shadow-[#10c0a5]/20">
                          <Users className="w-7 h-7 text-white" />
                        </div>
                        <div>
                          <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Your team & online presence
                          </h2>
                          <p className="text-slate-600">Help us understand your current setup</p>
                        </div>
                      </div>

                      {/* Agent Count */}
                      <div className="mb-6">
                        <label className="block text-sm font-medium text-slate-700 mb-3">
                          How many agents are in your organization?
                        </label>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {agentCountOptions.map((option) => (
                            <button
                              key={option.value}
                              onClick={() => setAgentCount(option.value)}
                              className={`
                                px-4 py-3 rounded-lg border-2 text-sm font-medium transition-all
                                ${agentCount === option.value
                                  ? 'border-[#10c0a5] bg-[#e6faf7]/50 text-[#10c0a5]'
                                  : 'border-slate-200 text-slate-600 hover:border-[#10c0a5]/50'
                                }
                              `}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Has Website */}
                      <div className="mb-6">
                        <label className="block text-sm font-medium text-slate-700 mb-3">
                          Do you currently have a website?
                        </label>
                        <div className="flex gap-3">
                          <button
                            onClick={() => setHasWebsite(true)}
                            className={`
                              flex-1 px-4 py-3 rounded-lg border-2 font-medium transition-all
                              ${hasWebsite === true
                                ? 'border-[#10c0a5] bg-[#e6faf7]/50 text-[#10c0a5]'
                                : 'border-slate-200 text-slate-600 hover:border-[#10c0a5]/50'
                              }
                            `}
                          >
                            Yes
                          </button>
                          <button
                            onClick={() => { setHasWebsite(false); setCurrentWebsite(''); }}
                            className={`
                              flex-1 px-4 py-3 rounded-lg border-2 font-medium transition-all
                              ${hasWebsite === false
                                ? 'border-[#10c0a5] bg-[#e6faf7]/50 text-[#10c0a5]'
                                : 'border-slate-200 text-slate-600 hover:border-[#10c0a5]/50'
                              }
                            `}
                          >
                            No
                          </button>
                        </div>
                      </div>

                      {/* Current Website URL */}
                      {hasWebsite && (
                        <div className="mb-6">
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            What's your website URL? *
                          </label>
                          <input
                            type="url"
                            value={currentWebsite}
                            onChange={handleWebsiteChange}
                            placeholder="https://www.yourwebsite.com"
                            className={`w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-[#10c0a5]/20 outline-none transition-all ${
                              websiteError ? 'border-red-400 focus:border-red-500' : 'border-slate-300 focus:border-[#10c0a5]'
                            }`}
                          />
                          {websiteError && (
                            <p className="mt-1 text-sm text-red-500">{websiteError}</p>
                          )}
                        </div>
                      )}

                      <div className="flex justify-between">
                        <button
                          onClick={goBack}
                          className="flex items-center gap-2 px-6 py-3 text-slate-600 hover:text-slate-900 font-medium transition-colors"
                        >
                          <ChevronLeft className="w-5 h-5" />
                          Back
                        </button>
                        <button
                          onClick={() => setCurrentStep(3)}
                          disabled={!canProceedStep2}
                          className="flex items-center gap-2 px-6 py-3 bg-[#10c0a5] hover:bg-[#0ea892] text-white font-medium rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Continue
                          <ChevronRight className="w-5 h-5" />
                        </button>
                      </div>
                    </motion.div>
                  )}

                  {/* Step 3: Marketing Experience & Budget */}
                  {currentStep === 3 && (
                    <motion.div
                      key="step3"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8"
                    >
                      <div className="flex items-center gap-4 mb-6">
                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#10c0a5] to-[#0d9488] flex items-center justify-center shadow-lg shadow-[#10c0a5]/20">
                          <DollarSign className="w-7 h-7 text-white" />
                        </div>
                        <div>
                          <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Marketing experience & budget
                          </h2>
                          <p className="text-slate-600">Let us know about your marketing background</p>
                        </div>
                      </div>

                      {/* Marketing Platforms */}
                      <div className="mb-6">
                        <label className="block text-sm font-medium text-slate-700 mb-3">
                          What marketing platforms have you used? (Select all that apply)
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                          {marketingPlatforms.map((platform) => (
                            <button
                              key={platform.id}
                              onClick={() => togglePlatform(platform.id)}
                              className={`
                                px-4 py-3 rounded-lg border-2 text-sm font-medium transition-all text-left
                                ${marketingExperience.includes(platform.id)
                                  ? 'border-[#10c0a5] bg-[#e6faf7]/50 text-[#10c0a5]'
                                  : 'border-slate-200 text-slate-600 hover:border-[#10c0a5]/50'
                                }
                              `}
                            >
                              {platform.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Budget */}
                      <div className="mb-6">
                        <label className="block text-sm font-medium text-slate-700 mb-3">
                          What's your monthly marketing budget?
                        </label>
                        <div className="space-y-2">
                          {budgetOptions.map((option) => (
                            <button
                              key={option.value}
                              onClick={() => setBudget(option.value)}
                              className={`
                                w-full px-4 py-3 rounded-lg border-2 text-sm font-medium transition-all text-left flex justify-between items-center
                                ${budget === option.value
                                  ? 'border-[#10c0a5] bg-[#e6faf7]/50 text-[#10c0a5]'
                                  : 'border-slate-200 text-slate-600 hover:border-[#10c0a5]/50'
                                }
                              `}
                            >
                              {option.label}
                              {option.qualified && budget === option.value && (
                                <span className="text-xs bg-[#10c0a5] text-white px-2 py-1 rounded-full">Great fit!</span>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="flex justify-between">
                        <button
                          onClick={goBack}
                          className="flex items-center gap-2 px-6 py-3 text-slate-600 hover:text-slate-900 font-medium transition-colors"
                        >
                          <ChevronLeft className="w-5 h-5" />
                          Back
                        </button>
                        <button
                          onClick={() => setCurrentStep(4)}
                          disabled={!canProceedStep3}
                          className="flex items-center gap-2 px-6 py-3 bg-[#10c0a5] hover:bg-[#0ea892] text-white font-medium rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Continue
                          <ChevronRight className="w-5 h-5" />
                        </button>
                      </div>
                    </motion.div>
                  )}

                  {/* Step 4: CRM & GoHighLevel */}
                  {currentStep === 4 && (
                    <motion.div
                      key="step4"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8"
                    >
                      <div className="flex items-center gap-4 mb-6">
                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#10c0a5] to-[#0d9488] flex items-center justify-center shadow-lg shadow-[#10c0a5]/20">
                          <Settings className="w-7 h-7 text-white" />
                        </div>
                        <div>
                          <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Your current systems
                          </h2>
                          <p className="text-slate-600">Tell us about your CRM and technology stack</p>
                        </div>
                      </div>

                      {/* Current CRM */}
                      <div className="mb-6">
                        <label className="block text-sm font-medium text-slate-700 mb-3">
                          What CRM system do you currently use?
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                          {crmOptions.map((option) => (
                            <button
                              key={option.value}
                              onClick={() => setCurrentCRM(option.value)}
                              className={`
                                px-4 py-3 rounded-lg border-2 text-sm font-medium transition-all
                                ${currentCRM === option.value
                                  ? 'border-[#10c0a5] bg-[#e6faf7]/50 text-[#10c0a5]'
                                  : 'border-slate-200 text-slate-600 hover:border-[#10c0a5]/50'
                                }
                              `}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                        {currentCRM === 'other' && (
                          <input
                            type="text"
                            value={otherCRM}
                            onChange={(e) => setOtherCRM(e.target.value)}
                            placeholder="Enter your CRM name"
                            className="mt-3 w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-[#10c0a5] focus:ring-2 focus:ring-[#10c0a5]/20 outline-none transition-all"
                          />
                        )}
                      </div>

                      {/* Open to GoHighLevel */}
                      <div className="mb-6">
                        <label className="block text-sm font-medium text-slate-700 mb-3">
                          Would you be open to using GoHighLevel as your CRM platform?
                        </label>
                        <p className="text-xs text-slate-500 mb-3">
                          GoHighLevel is our preferred platform for building complete marketing automation systems.
                        </p>
                        <div className="flex gap-3">
                          <button
                            onClick={() => setOpenToGoHighLevel(true)}
                            className={`
                              flex-1 px-4 py-3 rounded-lg border-2 font-medium transition-all
                              ${openToGoHighLevel === true
                                ? 'border-[#10c0a5] bg-[#e6faf7]/50 text-[#10c0a5]'
                                : 'border-slate-200 text-slate-600 hover:border-[#10c0a5]/50'
                              }
                            `}
                          >
                            Yes, I'm open to it
                          </button>
                          <button
                            onClick={() => setOpenToGoHighLevel(false)}
                            className={`
                              flex-1 px-4 py-3 rounded-lg border-2 font-medium transition-all
                              ${openToGoHighLevel === false
                                ? 'border-[#10c0a5] bg-[#e6faf7]/50 text-[#10c0a5]'
                                : 'border-slate-200 text-slate-600 hover:border-[#10c0a5]/50'
                              }
                            `}
                          >
                            I'd prefer to keep my current CRM
                          </button>
                        </div>
                      </div>

                      <div className="flex justify-between">
                        <button
                          onClick={goBack}
                          className="flex items-center gap-2 px-6 py-3 text-slate-600 hover:text-slate-900 font-medium transition-colors"
                        >
                          <ChevronLeft className="w-5 h-5" />
                          Back
                        </button>
                        <button
                          onClick={() => setCurrentStep(5)}
                          disabled={!canProceedStep4}
                          className="flex items-center gap-2 px-6 py-3 bg-[#10c0a5] hover:bg-[#0ea892] text-white font-medium rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Continue
                          <ChevronRight className="w-5 h-5" />
                        </button>
                      </div>
                    </motion.div>
                  )}

                  {/* Step 5: Contact Info */}
                  {currentStep === 5 && (
                    <motion.div
                      key="step5"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8"
                    >
                      <div className="flex items-center gap-4 mb-6">
                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#10c0a5] to-[#0d9488] flex items-center justify-center shadow-lg shadow-[#10c0a5]/20">
                          <UserCircle className="w-7 h-7 text-white" />
                        </div>
                        <div>
                          <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Your contact information
                          </h2>
                          <p className="text-slate-600">Let us know how to reach you</p>
                        </div>
                      </div>

                      <form onSubmit={contactForm.handleSubmit(handleContactSubmit)} className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            Your Name *
                          </label>
                          <input
                            {...contactForm.register('name', { required: true })}
                            type="text"
                            placeholder="John Smith"
                            className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-[#10c0a5] focus:ring-2 focus:ring-[#10c0a5]/20 outline-none transition-all"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            Company Name *
                          </label>
                          <input
                            {...contactForm.register('company_name', { required: true })}
                            type="text"
                            placeholder="Your Insurance Agency"
                            className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-[#10c0a5] focus:ring-2 focus:ring-[#10c0a5]/20 outline-none transition-all"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            Business Website
                          </label>
                          <input
                            {...contactForm.register('website', {
                              validate: (value) => !value || validateWebsite(value) || 'Please enter a valid website URL'
                            })}
                            type="url"
                            inputMode="url"
                            placeholder="https://www.yourcompany.com"
                            onChange={(e) => {
                              contactForm.setValue('website', e.target.value);
                              if (e.target.value && !validateWebsite(e.target.value)) {
                                setContactWebsiteError('Please enter a valid website URL (e.g., https://example.com)');
                              } else {
                                setContactWebsiteError('');
                              }
                            }}
                            className={`w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-[#10c0a5]/20 outline-none transition-all ${
                              contactWebsiteError ? 'border-red-400 focus:border-red-500' : 'border-slate-300 focus:border-[#10c0a5]'
                            }`}
                          />
                          {contactWebsiteError && (
                            <p className="mt-1 text-sm text-red-500">{contactWebsiteError}</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            Phone Number *
                          </label>
                          <input
                            {...contactForm.register('phone', { required: true })}
                            type="tel"
                            inputMode="tel"
                            placeholder="(555) 123-4567"
                            onChange={(e) => {
                              const formatted = formatPhoneNumber(e.target.value);
                              contactForm.setValue('phone', formatted);
                              const validation = validatePhone(formatted);
                              setPhoneError(validation.error);
                            }}
                            className={`w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-[#10c0a5]/20 outline-none transition-all ${
                              phoneError ? 'border-red-400 focus:border-red-500' : 'border-slate-300 focus:border-[#10c0a5]'
                            }`}
                          />
                          {phoneError && (
                            <p className="mt-1 text-sm text-red-500">{phoneError}</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            Email Address *
                          </label>
                          <input
                            {...contactForm.register('email', { required: true })}
                            type="email"
                            inputMode="email"
                            placeholder="john@yourcompany.com"
                            onChange={(e) => {
                              contactForm.setValue('email', e.target.value);
                              if (e.target.value && !validateEmail(e.target.value)) {
                                setEmailError('Please enter a valid email address');
                              } else {
                                setEmailError('');
                              }
                            }}
                            className={`w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-[#10c0a5]/20 outline-none transition-all ${
                              emailError ? 'border-red-400 focus:border-red-500' : 'border-slate-300 focus:border-[#10c0a5]'
                            }`}
                          />
                          {emailError && (
                            <p className="mt-1 text-sm text-red-500">{emailError}</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            Tell us more about what you're looking for
                          </label>
                          <textarea
                            {...contactForm.register('additional_details')}
                            rows={4}
                            placeholder="What are your biggest marketing challenges? What does success look like for you? Any specific goals or timelines?"
                            className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-[#10c0a5] focus:ring-2 focus:ring-[#10c0a5]/20 outline-none transition-all resize-none"
                          />
                        </div>

                        <div className="flex justify-between pt-4">
                          <button
                            type="button"
                            onClick={goBack}
                            className="flex items-center gap-2 px-6 py-3 text-slate-600 hover:text-slate-900 font-medium transition-colors"
                          >
                            <ChevronLeft className="w-5 h-5" />
                            Back
                          </button>
                          <button
                            type="submit"
                            disabled={isSubmitting || phoneError || emailError || contactWebsiteError}
                            className="flex items-center gap-2 px-8 py-3 bg-[#10c0a5] hover:bg-[#0ea892] text-white font-semibold rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isSubmitting ? (
                              <>
                                <Loader2 className="w-5 h-5 animate-spin" />
                                Submitting...
                              </>
                            ) : (
                              <>
                                Submit Inquiry
                                <ChevronRight className="w-5 h-5" />
                              </>
                            )}
                          </button>
                        </div>
                      </form>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </section>
          )}
        </>
      ) : (
        /* Success Page */
        <section className="py-16 sm:py-24 px-4">
          <div className="max-w-2xl mx-auto text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="w-20 h-20 bg-[#e6faf7] rounded-full flex items-center justify-center mx-auto mb-6"
            >
              <CheckCircle2 className="w-10 h-10 text-[#10c0a5]" />
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Thanks for Your Interest!
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-lg text-slate-600 mb-8"
            >
              We've received your inquiry and will review your information shortly. 
              If we think we're a good fit, one of our team members will reach out within 1-2 business days.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-[#e6faf7]/50 rounded-xl p-6 mb-8"
            >
              <h2 className="font-semibold text-slate-900 mb-3">What happens next?</h2>
              <ul className="text-sm text-slate-600 space-y-2 text-left max-w-md mx-auto">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#10c0a5] mt-0.5 flex-shrink-0" />
                  <span>We'll review your marketing goals and current setup</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#10c0a5] mt-0.5 flex-shrink-0" />
                  <span>If we're a good match, we'll schedule a discovery call</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#10c0a5] mt-0.5 flex-shrink-0" />
                  <span>We'll create a custom marketing strategy for your agency</span>
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <a
                href="https://www.quoteplicity.com/"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#10c0a5] hover:bg-[#0ea892] text-white font-medium rounded-xl transition-colors"
              >
                <Globe className="w-5 h-5" />
                Visit Quoteplicity
              </a>
              <a
                href="https://www.quoteplicity.com/lp-book-call"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-[#10c0a5] text-[#10c0a5] hover:bg-[#e6faf7] font-medium rounded-xl transition-colors"
              >
                <Calendar className="w-5 h-5" />
                Book a Call
              </a>
            </motion.div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 py-8 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <a href="https://www.quoteplicity.com/" target="_blank" rel="noopener noreferrer">
            <QuoteplicityLogo />
          </a>
          <p className="text-sm text-slate-500 mt-4">
            Quoteplicity Marketing Agency provides full-service digital marketing exclusively for life insurance professionals.
          </p>
          <p className="text-xs text-slate-400 mt-2">
            © 2026 Quoteplicity. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default MarketingAgencyPage;
